import StackIcon from '@/components/content/stacks/shared/StackIcon'
import StackIconLarge from '@/components/content/stacks/shared/StackIconLarge'
import AppIcon from '@/components/content/app/AppIcon'
import Link from '@/components/ui/Link'
import Pill from '@/components/ui/Pill'
import Badge from '@/components/ui/Badge'
import { getBrandColors, isPopulatedObject } from '@/lib/utils'
import { Icon } from '@/icons/Icon'
import { FeaturedTagType } from '@/collections/FeaturedTag/constants'
import type { App, Media, Stack, StackAppsApp } from '@/payload-types'

type StackWithKeys = Pick<
  Stack,
  | 'id'
  | 'slug'
  | 'icon'
  | 'name'
  | 'description'
  | 'summary'
  | 'notes'
  | 'stackApps'
  | 'stackFaqs'
  | 'updatedAt'
  | 'lastPublishedAt'
  | 'companyStackApp'
>

export default function StackThumbnail({ stack, type }: { stack: StackWithKeys; type?: string }) {
  const stackCompanyApp = stack.companyStackApp as App

  const link = `/stacks/${stack.slug}`

  const appsInStack = stack.stackApps?.docs?.map((sa) => (sa as StackAppsApp).app as App) ?? []

  const stackDealsCount = appsInStack?.reduce((total, app) => {
    const appDeals = app.deals?.docs?.length ?? 0
    return total + appDeals
  }, 0)

  const details = (
    <div className="flex flex-col text-white [&>p>span]:text-zinc-500">
      <Link href={link} className="line-clamp-1 w-fit text-20 font-semibold">
        {stack.name}
      </Link>
      <p className="text-13">
        {appsInStack?.length ?? 0} app{(appsInStack?.length ?? 0) === 1 ? '' : 's'}
        <span>
          {' '}
          and {stackDealsCount} deal{stackDealsCount === 1 ? '' : 's'}
        </span>
      </p>
    </div>
  )

  const buttons = (
    <div className="relative z-10 flex gap-3 px-0.5">
      <Link href={link}>
        <Pill style="primary" className="ml-auto">
          See Apps Stack
        </Pill>
      </Link>
    </div>
  )

  const stackIcon = stackCompanyApp ? (
    <StackIcon apps={[stackCompanyApp]} />
  ) : (
    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/15 bg-black text-white drop-shadow-md">
      <Icon name={stack.icon || ''} />
    </div>
  )

  switch (type) {
    case FeaturedTagType.ThumbnailPrimary:
      return (
        <div
          key={stack.id}
          className="group relative flex h-[300px] w-full flex-col justify-center gap-5 overflow-hidden rounded-2xl bg-black bg-gradient-to-br from-transparent from-25% to-pink-500/40 px-6 py-10"
        >
          <div className="relative z-10 flex flex-col gap-5">
            <Badge style="primary">New!</Badge>
            {details}
          </div>
          {buttons}

          <div className="absolute -right-16 top-7 flex h-full flex-col gap-12 transition-all duration-500 ease-in-out group-hover:top-2 group-hover:gap-16 xs:-right-10 md:right-[15%]">
            {appsInStack.slice(0, 4).map((app: App, index: number) => {
              const appIcon = app.icon as Media
              const appGlyph = app.glyph as Media
              if (!isPopulatedObject<Media>(appIcon) && !isPopulatedObject<Media>(appGlyph)) {
                return null
              }
              const { backgroundColor } = getBrandColors(app)
              return (
                <div
                  key={`${app.id}-${index}`}
                  style={{ zIndex: appsInStack.length - index }}
                  className="-mt-28 -rotate-[35deg] skew-x-[24deg] first:mt-0"
                >
                  <AppIcon
                    glyph={appGlyph}
                    icon={appIcon}
                    name={app.name}
                    size={128}
                    invert={true}
                    background={backgroundColor}
                    className="border border-primary-500 border-opacity-0 drop-shadow-marquee"
                  />
                </div>
              )
            })}
          </div>
        </div>
      )
    case FeaturedTagType.ThumbnailSecondary:
      return (
        <div
          key={stack.id}
          className="bg-gradient group relative flex h-[440px] w-full flex-col items-center justify-between gap-5 overflow-hidden rounded-2xl bg-gradient-to-t px-5 py-10"
        >
          <div className="relative z-20 flex flex-col items-center gap-5">
            <div className="relative">
              <StackIconLarge apps={appsInStack} />
              <div className="absolute left-1/2 top-1/2 z-10 flex h-12 w-full -translate-x-1/2 -translate-y-1/2 justify-center gap-5 opacity-70">
                {appsInStack?.map((app) => {
                  const appIcon = app.icon as Media
                  if (!isPopulatedObject<Media>(appIcon)) {
                    return null
                  }
                  const { backgroundColor } = getBrandColors(app)
                  return (
                    <AppIcon key={app.id} icon={appIcon} size={48} background={backgroundColor} />
                  )
                })}
              </div>
            </div>

            {stackIcon}
          </div>

          <div className="flex flex-col items-center gap-4 text-center">
            {details}
            {buttons}
          </div>
        </div>
      )
    default:
      return (
        <Link
          href={link}
          key={stack.id}
          className="group flex items-center justify-between gap-5 md:gap-10"
        >
          <div className="flex w-full items-center gap-3.5 md:w-[calc(100%-90px)]">
            <StackIcon apps={appsInStack} />

            <div className="mt-1 flex flex-col gap-1">
              <div className="flex h-5 w-full items-center gap-2.5">
                <span className="line-clamp-1 font-semibold text-gray-950">{stack.name}</span>
              </div>
              {stack.description && (
                <p className="line-clamp-1 text-13 text-gray-600">{stack.description}</p>
              )}
              <div className="line-clamp-1 w-full text-13 text-gray-400">
                <span className="text-gray-600">
                  {appsInStack?.length ?? 0} app{(appsInStack?.length ?? 0) === 1 ? '' : 's'},
                </span>{' '}
                and {stackDealsCount} deal{stackDealsCount === 1 ? '' : 's'}
              </div>
            </div>
          </div>

          <Pill as="span" style="outlinePrimary" className="ml-auto hidden sm:flex">
            See
          </Pill>
        </Link>
      )
  }
}
